// Firebase Materials Service
// ==========================
// CRUD operations for journey materials (uploaded files for learning plan generation)

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  Timestamp,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from './config';
import { where, orderBy } from './base';
import type { JourneyMaterial, MaterialFileType } from '../../types';
import { LIMITS } from '../../config/limits';
import i18n from '../../i18n';

/**
 * Get collection reference for journey materials
 * Materials are stored in a top-level collection for easier querying
 */
const getJourneyMaterialsRef = () => collection(db, 'journeyMaterials');

/**
 * Upload a file to Firebase Storage and create a Firestore record
 * @param userId The user's ID
 * @param journeyId The learning journey's ID
 * @param file The file to upload
 * @returns Result with success status and material data
 */
export const uploadJourneyMaterial = async (
  userId: string,
  journeyId: string,
  file: File
): Promise<{ success: boolean; material?: JourneyMaterial; error?: string }> => {
  try {
    // Validate file type
    const extension = file.name.split('.').pop()?.toLowerCase();
    const validTypes: MaterialFileType[] = ['pdf', 'docx', 'txt'];

    if (!extension || !validTypes.includes(extension as MaterialFileType)) {
      return {
        success: false,
        error: `Ugyldigt filformat. Understøttede formater: ${validTypes.join(', ')}`,
      };
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return {
        success: false,
        error: 'Filen er for stor. Maksimal størrelse er 10MB.',
      };
    }

    // Check material count limit per journey
    const countQuery = query(
      getJourneyMaterialsRef(),
      where('userId', '==', userId),
      where('journeyId', '==', journeyId)
    );
    const countSnapshot = await getDocs(countQuery);
    if (countSnapshot.size >= LIMITS.MAX_MATERIALS_PER_JOURNEY) {
      return {
        success: false,
        error: i18n.t('common:limits.maxMaterialsReached'),
      };
    }

    // Generate unique storage path
    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storagePath = `journeyMaterials/${userId}/${journeyId}/${timestamp}_${sanitizedFileName}`;

    // Upload file to Firebase Storage
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);

    // Get download URL
    const downloadUrl = await getDownloadURL(storageRef);

    // Create Firestore document
    const materialData = {
      userId,
      journeyId,
      fileName: file.name,
      fileType: extension as MaterialFileType,
      fileSize: file.size,
      storagePath,
      downloadUrl,
      createdAt: Timestamp.now(),
    };

    const docRef = await addDoc(getJourneyMaterialsRef(), materialData);

    const material: JourneyMaterial = {
      id: docRef.id,
      ...materialData,
      createdAt: new Date(),
    };

    console.log('[Firebase] Material uploaded:', material.id);
    return { success: true, material };
  } catch (error: any) {
    console.error('[Firebase] Error uploading material:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all materials for a specific journey
 * @param userId The user's ID
 * @param journeyId The learning journey's ID
 * @returns Array of journey materials
 */
export const getJourneyMaterials = async (
  userId: string,
  journeyId: string
): Promise<{ success: boolean; materials?: JourneyMaterial[]; error?: string }> => {
  try {
    const q = query(
      getJourneyMaterialsRef(),
      where('userId', '==', userId),
      where('journeyId', '==', journeyId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const materials = snapshot.docs.map(docItem => ({
      id: docItem.id,
      ...docItem.data(),
    })) as JourneyMaterial[];

    return { success: true, materials };
  } catch (error: any) {
    console.error('[Firebase] Error getting journey materials:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a single material by ID
 * @param materialId The material's ID
 * @returns The material or null if not found
 */
export const getJourneyMaterial = async (
  materialId: string
): Promise<{ success: boolean; material?: JourneyMaterial; error?: string }> => {
  try {
    const docRef = doc(db, 'journeyMaterials', materialId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return { success: false, error: 'Material not found' };
    }

    return {
      success: true,
      material: { id: docSnap.id, ...docSnap.data() } as JourneyMaterial,
    };
  } catch (error: any) {
    console.error('[Firebase] Error getting material:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update a material's extracted text (after parsing)
 * @param materialId The material's ID
 * @param extractedText The text extracted from the file
 * @returns Result with success status
 */
export const updateMaterialExtractedText = async (
  materialId: string,
  extractedText: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const docRef = doc(db, 'journeyMaterials', materialId);
    await updateDoc(docRef, {
      extractedText,
      updatedAt: Timestamp.now(),
    });
    return { success: true };
  } catch (error: any) {
    console.error('[Firebase] Error updating material text:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a material from Storage and Firestore
 * @param userId The user's ID (for authorization check)
 * @param materialId The material's ID
 * @returns Result with success status
 */
export const deleteJourneyMaterial = async (
  userId: string,
  materialId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get the material to verify ownership and get storage path
    const result = await getJourneyMaterial(materialId);

    if (!result.success || !result.material) {
      return { success: false, error: 'Material not found' };
    }

    if (result.material.userId !== userId) {
      return { success: false, error: 'Unauthorized' };
    }

    // Delete from Storage
    try {
      const storageRef = ref(storage, result.material.storagePath);
      await deleteObject(storageRef);
    } catch (storageError: any) {
      // Log but continue if storage deletion fails (file might not exist)
      console.warn('[Firebase] Error deleting from storage:', storageError);
    }

    // Delete from Firestore
    const docRef = doc(db, 'journeyMaterials', materialId);
    await deleteDoc(docRef);

    console.log('[Firebase] Material deleted:', materialId);
    return { success: true };
  } catch (error: any) {
    console.error('[Firebase] Error deleting material:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete all materials for a journey (used when deleting a journey)
 * @param userId The user's ID
 * @param journeyId The journey's ID
 * @returns Result with success status and count of deleted materials
 */
export const deleteAllJourneyMaterials = async (
  userId: string,
  journeyId: string
): Promise<{ success: boolean; deletedCount?: number; error?: string }> => {
  try {
    const result = await getJourneyMaterials(userId, journeyId);

    if (!result.success || !result.materials) {
      return { success: true, deletedCount: 0 };
    }

    let deletedCount = 0;
    for (const material of result.materials) {
      const deleteResult = await deleteJourneyMaterial(userId, material.id);
      if (deleteResult.success) {
        deletedCount++;
      }
    }

    console.log(`[Firebase] Deleted ${deletedCount} materials for journey ${journeyId}`);
    return { success: true, deletedCount };
  } catch (error: any) {
    console.error('[Firebase] Error deleting all journey materials:', error);
    return { success: false, error: error.message };
  }
};
